import React, { useEffect, useId, useRef, useState } from 'react';
import { buttonClass, cx, popoverClass, popoverItemClass } from './Primitives';
import { IconChevronLeft, IconChevronRight } from './icons';
import { useIndicator } from './useIndicator';

/* ============================================================================
   Navigation inside a page (DESIGN-SYSTEM §10.3): tabs, a trail, a pager and
   a menu. The product's places are the shell's; these move within one.
   ============================================================================ */

/* ---------------------------------- Tabs ---------------------------------- */

/**
 * Tabs for views of one thing that are read one at a time (an account's
 * history and its documents). A real tab list: arrow keys move between the
 * tabs, the chosen one is the tab stop, and its panel is labelled by it. For
 * filters over one list, use `Segmented` (buttons that are pressed) instead:
 * a filter does not swap the panel, it narrows it.
 */
export function Tabs<T extends string>({
    label, tabs, value, onChange, className, listClassName, children,
}: {
    label: string;
    tabs: readonly { value: T; label: React.ReactNode; count?: number }[];
    value: T;
    onChange: (value: T) => void;
    className?: string;
    /** Classes for the row of tabs itself (an inset inside a surface). */
    listClassName?: string;
    /** The chosen tab's panel. */
    children?: React.ReactNode;
}) {
    const base = useId();
    // The chosen tab's underline is one mark that slides between tabs, never a border that blinks off and on.
    const { track: list, style, shown, animate } = useIndicator<HTMLDivElement>(value);
    const onKeyDown = (e: React.KeyboardEvent) => {
        const at = tabs.findIndex(t => t.value === value);
        const to = e.key === 'ArrowRight' ? at + 1 : e.key === 'ArrowLeft' ? at - 1 : e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : null;
        if (to === null) return;
        e.preventDefault();
        const next = tabs[(to + tabs.length) % tabs.length]!;
        onChange(next.value);
        list.current?.querySelector<HTMLButtonElement>(`[data-tab="${next.value}"]`)?.focus();
    };
    return (
        <div className={className}>
            <div ref={list} role="tablist" aria-label={label} onKeyDown={onKeyDown}
                className={cx('relative flex items-end gap-1 border-b border-separator overflow-x-auto', listClassName)}>
                {shown && (
                    <span aria-hidden="true" data-indicator="" style={style}
                        className={cx('absolute left-0 bottom-0 h-0.5 rounded-full bg-accent', animate && 'transition-[transform,width] duration-200 ease-out-quart')} />
                )}
                {tabs.map(t => {
                    const on = t.value === value;
                    return (
                        <button key={t.value} type="button" role="tab" data-tab={t.value} data-active={on}
                            id={`${base}-tab-${t.value}`} aria-controls={`${base}-panel`} aria-selected={on} tabIndex={on ? 0 : -1}
                            onClick={() => onChange(t.value)}
                            className={cx(
                                'relative inline-flex items-center gap-2 h-10 px-3 text-body font-semibold whitespace-nowrap transition-colors duration-150',
                                on ? 'text-label' : 'text-label-3 hover:text-label',
                            )}>
                            {t.label}
                            {/* A count is a figure beside the word, not a pill: no fill to swap from one tab to the next. */}
                            {t.count !== undefined && (
                                <span className="num text-secondary font-medium text-label-3">{t.count}</span>
                            )}
                        </button>
                    );
                })}
            </div>
            {children !== undefined && (
                <div role="tabpanel" id={`${base}-panel`} aria-labelledby={`${base}-tab-${value}`} tabIndex={0} className="focus-visible:outline-offset-4">
                    {children}
                </div>
            )}
        </div>
    );
}

/* ------------------------------- Breadcrumbs ------------------------------- */

/** Where this page sits, for a page two levels down (Settings › Team). The last item is the page itself. */
export function Breadcrumbs({ items, className }: {
    items: readonly { label: string; href?: string; render?: (children: React.ReactNode, className: string) => React.ReactNode }[];
    className?: string;
}) {
    return (
        <nav aria-label="Breadcrumb" className={className}>
            <ol className="flex items-center flex-wrap gap-1 text-secondary">
                {items.map((item, i) => {
                    const last = i === items.length - 1;
                    const linkClass = 'text-label-3 hover:text-label hover:underline underline-offset-4';
                    return (
                        <li key={`${item.label}-${i}`} className="flex items-center gap-1 min-w-0">
                            {last
                                ? <span aria-current="page" className="font-semibold text-label truncate">{item.label}</span>
                                : item.render
                                    ? item.render(item.label, linkClass)
                                    : <a href={item.href} className={linkClass}>{item.label}</a>}
                            {!last && <span className="text-label-4 flex-none" aria-hidden="true"><IconChevronRight size={12} /></span>}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
}

/* ---------------------------------- Pager ---------------------------------- */

/**
 * "12 of 25" with Previous and Next: moving through a list one record at a
 * time, or one page of rows at a time. The position is said as text; the
 * buttons say where they go, and are disabled, not hidden, at either end.
 */
export function Pager({ position, total, onPrevious, onNext, noun = 'of', className }: {
    position: number;
    total: number;
    onPrevious?: () => void;
    onNext?: () => void;
    /** The words between the numbers ("of"). */
    noun?: string;
    className?: string;
}) {
    return (
        <div className={cx('flex items-center gap-2', className)}>
            <span className="text-secondary text-label-3 num whitespace-nowrap" aria-live="polite">{position} {noun} {total}</span>
            <button type="button" onClick={onPrevious} disabled={!onPrevious || position <= 1}
                className={buttonClass({ variant: 'secondary', size: 'sm' })}>
                <IconChevronLeft /> Previous
            </button>
            <button type="button" onClick={onNext} disabled={!onNext || position >= total}
                className={buttonClass({ variant: 'secondary', size: 'sm' })}>
                Next <IconChevronRight />
            </button>
        </div>
    );
}

/* ---------------------------------- Menu ----------------------------------- */

/**
 * A button that opens a short list of actions (Export, Rename, Disconnect).
 * The list is a real menu: it opens on Enter, Space or the down arrow with the
 * first item focused, the arrows move through it, Esc or a press outside
 * closes it and focus goes back to the button. Each item is a button.
 *
 * The list is placed against the window, not inside the button's box, so a
 * table that scrolls sideways never cuts it off; it opens upward when there is
 * no room below, and a scroll or a resize closes it rather than leaving it
 * floating away from its button. `name` is the button's accessible name when
 * its label is only an icon (a row's "More actions").
 */
export function MenuButton({ label, name, items, align = 'end', variant = 'secondary', size = 'md', className, buttonClassName }: {
    label: React.ReactNode;
    name?: string;
    items: readonly { label: React.ReactNode; onSelect: () => void; tone?: 'dang'; disabled?: boolean }[];
    align?: 'start' | 'end';
    variant?: 'secondary' | 'quiet';
    size?: 'sm' | 'md';
    className?: string;
    buttonClassName?: string;
}) {
    const [open, setOpen] = useState(false);
    const [at, setAt] = useState<React.CSSProperties>({});
    const id = useId();
    const button = useRef<HTMLButtonElement>(null);
    const menu = useRef<HTMLDivElement>(null);
    const items$ = () => Array.from(menu.current?.querySelectorAll<HTMLButtonElement>('[role=menuitem]:not(:disabled)') ?? []);
    const place = () => {
        const r = button.current?.getBoundingClientRect();
        if (!r || typeof window === 'undefined') return;
        const below = window.innerHeight - r.bottom;
        const up = below < 44 * items.length + 24 && r.top > below;
        setAt({
            position: 'fixed',
            ...(up ? { bottom: window.innerHeight - r.top + 6 } : { top: r.bottom + 6 }),
            ...(align === 'end' ? { right: Math.max(8, window.innerWidth - r.right) } : { left: Math.max(8, r.left) }),
        });
    };
    const toggle = () => { if (!open) place(); setOpen(!open); };
    useEffect(() => {
        if (!open) return;
        items$()[0]?.focus();
        const away = (e: MouseEvent) => { if (!menu.current?.contains(e.target as Node) && !button.current?.contains(e.target as Node)) setOpen(false); };
        // A scroll inside the list itself keeps it; any other (the page, a table scrolled sideways) closes it.
        const gone = (e: Event) => { if (!(e.target instanceof Node && menu.current?.contains(e.target))) setOpen(false); };
        document.addEventListener('mousedown', away);
        window.addEventListener('scroll', gone, true);
        window.addEventListener('resize', gone);
        return () => {
            document.removeEventListener('mousedown', away);
            window.removeEventListener('scroll', gone, true);
            window.removeEventListener('resize', gone);
        };
    }, [open]);
    const close = (refocus = true) => { setOpen(false); if (refocus) button.current?.focus(); };
    const onMenuKey = (e: React.KeyboardEvent) => {
        const list = items$();
        const at = list.indexOf(document.activeElement as HTMLButtonElement);
        if (e.key === 'Escape') { e.preventDefault(); close(); return; }
        if (e.key === 'Tab') { close(false); return; }
        const to = e.key === 'ArrowDown' ? at + 1 : e.key === 'ArrowUp' ? at - 1 : e.key === 'Home' ? 0 : e.key === 'End' ? list.length - 1 : null;
        if (to === null) return;
        e.preventDefault();
        list[(to + list.length) % list.length]?.focus();
    };
    return (
        <div className={cx('relative inline-block', className)}>
            <button ref={button} type="button" aria-haspopup="menu" aria-expanded={open} aria-controls={open ? id : undefined}
                aria-label={name} title={name}
                onClick={toggle}
                onKeyDown={e => { if (e.key === 'ArrowDown') { e.preventDefault(); place(); setOpen(true); } }}
                className={buttonClass({ variant, size, className: buttonClassName })}>
                {label}
            </button>
            {open && (
                <div ref={menu} id={id} role="menu" aria-label={name} onKeyDown={onMenuKey} style={at}
                    className={cx('z-40 min-w-48 p-1', popoverClass)}>
                    {items.map((item, i) => (
                        <button key={i} type="button" role="menuitem" disabled={item.disabled}
                            onClick={() => { close(); item.onSelect(); }}
                            className={cx(popoverItemClass, 'font-medium', item.tone === 'dang' ? 'text-dang' : 'text-label')}>
                            {item.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
